import { useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useUserStore } from '@/store/user'
import { useServerWorkStore } from '@/store/useServerWorkStore'
import { getPushPromptPolicy, usePushPromptStore } from '@/store/usePushPromptStore'
import { subscribePushDeviceMarker } from '@/lib/push/pushDeviceMarker'
import { isPushRegisteredOnThisDevice, registerPush } from '@/lib/push/registerPush'
import { getPushPermission, type PushPermission } from '@/lib/push/pushPermission'
import { decidePushAutoRegister } from '@/lib/push/pushAutoRegisterDecision'
import {
  myNotificationPreferenceQuery,
  wantsPushAnywhere,
} from '@/routes/settings/notifications/actions'

/**
 * Re-establishes this device's push registration after a login, silently.
 *
 * Logging out deletes the registration on purpose — it binds one browser to one
 * signed-in user, and leaving it would keep delivering the previous user's
 * notifications, previews and all, to whoever holds the machine next. The
 * missing half was the way back in: nothing re-registered on login, so push
 * silently stopped and the settings screen reported it as off. Registration is
 * session state, not onboarding state.
 *
 * Mounted once near the root, beside usePushPromptGate. It deliberately does NOT
 * live inside that hook: this one issues a network write, and folding a side
 * effect into a hook whose job is deciding whether to show a card makes both
 * harder to reason about. The two do interact, but only through state — once a
 * registration exists, decidePushPrompt returns `already-registered` on its own,
 * so nothing here has to suppress the card explicitly.
 */
export function usePushAutoRegister({ isOnline }: { isOnline: boolean }): void {
  const userId = useUserStore(s => s.user?.id ?? null)
  const byUser = usePushPromptStore(s => s.byUser)
  const isServerWorkInFlight = useServerWorkStore(s => s.inFlight > 0)

  const [permission, setPermission] = useState<PushPermission | null>(null)
  // Bumped when the registration marker changes, so a rotation that retires this
  // device's registration is noticed here too. localStorage is not reactive, and
  // the marker is read at evaluation time rather than render time.
  const [markerTick, setMarkerTick] = useState(0)

  // One attempt per user per mount. registerPush() is a network write, and this
  // effect can re-run on marker changes and on the preference query settling —
  // without this guard a persistent server-side failure would be retried in a
  // loop rather than once, quietly. A genuine retry has its own triggers: the
  // next login, or the user pressing Enable.
  const attemptedForUser = useRef<number | null>(null)

  useEffect(() => {
    if (userId === null) {
      setPermission(null)
      attemptedForUser.current = null
      return
    }

    let cancelled = false
    void getPushPermission()
      .then(next => {
        if (!cancelled) setPermission(next)
      })
      .catch(() => {
        if (!cancelled) setPermission('unsupported')
      })

    return () => {
      cancelled = true
    }
  }, [userId])

  useEffect(() => {
    const onMarkerChange = () => setMarkerTick(tick => tick + 1)
    return subscribePushDeviceMarker(onMarkerChange)
  }, [])

  // Only ask the server about preferences when the cheap local checks already
  // point at registering. Fetching for every user on every load would put a
  // request behind a decision that is usually "no" — and the answer is only ever
  // consulted in the one branch below.
  const localChecksPass =
    userId !== null &&
    isOnline &&
    permission === 'granted' &&
    !isPushRegisteredOnThisDevice(userId) &&
    !getPushPromptPolicy(byUser, userId).dismissedForever &&
    // The preference GET would queue behind the render just like the POST, and
    // with `retry: false` a timed-out fetch leaves `preference` undefined, so the
    // registration would never resume. Fetch it after the work settles.
    !isServerWorkInFlight

  const { data: preference, isPending } = useQuery({
    ...myNotificationPreferenceQuery(),
    enabled: localChecksPass,
  })

  useEffect(() => {
    if (permission === null) return
    if (userId !== null && attemptedForUser.current === userId) return

    const decision = decidePushAutoRegister({
      hasUser: userId !== null,
      isOnline,
      permission,
      // Read at evaluation time: localStorage is not reactive, and markerTick is
      // what brings us back here when it changes.
      isRegisteredOnThisDevice: userId !== null && isPushRegisteredOnThisDevice(userId),
      policy: getPushPromptPolicy(byUser, userId),
      // `undefined` covers both "not fetched because the local checks failed"
      // and "still in flight" — neither is consent, and the decision maps null
      // to `preference-unknown` rather than proceeding.
      wantsPush: preference ? wantsPushAnywhere(preference) : null,
      isServerWorkInFlight,
      now: Date.now(),
    })

    if (!decision.register) return

    attemptedForUser.current = userId
    // Fire and forget: a failure here must never surface UI. The user has not
    // asked for anything at this moment, so an error toast would be noise about
    // an action they did not take. The soft prompt remains their explicit path.
    void registerPush(userId as number).catch(() => undefined)
  }, [
    userId,
    isOnline,
    permission,
    byUser,
    preference,
    isPending,
    markerTick,
    isServerWorkInFlight,
  ])
}
